import { useState } from "react";

const K = {
  bg: '#fefae0',
  surface: '#f2efd5',
  surfaceHigh: '#e8e0c0',
  red: '#b7102a',
  amber: '#ffb702',
  text: '#1a1107',
  muted: '#7a6b4f',
  font: "'Lexend', 'Plus Jakarta Sans', sans-serif",
};

const Section = ({ children, style, id }) => (
  <section id={id} style={{
    maxWidth: 900,
    margin: '0 auto',
    padding: '72px 32px',
    scrollMarginTop: 80,
    ...style
  }}>
    {children}
  </section>
);

const Tag = ({ children }) => (
  <span style={{
    display: 'inline-block',
    background: K.amber,
    color: K.text,
    fontFamily: K.font,
    fontWeight: 600,
    fontSize: '0.75rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '4px 12px',
    borderRadius: 999,
    marginBottom: 16,
  }}>
    {children}
  </span>
);

const H2 = ({ children }) => (
  <h2 style={{
    fontFamily: K.font,
    fontWeight: 700,
    fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
    color: K.text,
    marginTop: 0,
    marginBottom: 24,
    lineHeight: 1.2,
  }}>
    {children}
  </h2>
);

const Body = ({ children, style }) => (
  <p style={{
    fontFamily: K.font,
    fontSize: '1.0625rem',
    lineHeight: 1.75,
    color: K.muted,
    margin: '0 0 16px 0',
    ...style,
  }}>
    {children}
  </p>
);

const Card = ({ children, style }) => (
  <div style={{
    background: K.surface,
    borderRadius: 20,
    padding: '32px 36px',
    marginBottom: 16,
    ...style,
  }}>
    {children}
  </div>
);

const Divider = () => (
  <div style={{ borderTop: `1.5px solid ${K.surfaceHigh}` }} />
);

const content = {
  no: {
    heroTag: 'Leseapp for barn',
    heroH1a: 'Lesetrening som møter',
    heroH1b: 'barnet der det er.',
    heroSub: 'En AI-drevet leseveileder for barn med høyt skolefravær — designet for å fylle kunnskapshull, ett steg av gangen.',
    heroCta: 'Beta H2 2026',
    heroKompis: '🐙 Med Kompis',

    problemTag: 'Problemet',
    problemH2: 'Den onde sirkelen',
    problemCards: [
      { step: '01', title: 'Fravær', text: 'Barnet er mye borte fra skolen — av helsemessige, sosiale eller psykiske årsaker.' },
      { step: '02', title: 'Kunnskapsgap', text: 'Fravær fører til hull i basisferdigheter. Jo mer borte, jo større gap.' },
      { step: '03', title: 'Manglende mestring', text: 'Når barnet er tilstede, henger det ikke med. Lesing og oppgaver oppleves overveldende.' },
      { step: '04', title: 'Angst og tilbaketrekking', text: 'Følelse av utilstrekkelighet skaper angst. Barnet trekker seg unna — eller forstyrrer for å skjule det.' },
      { step: '05', title: 'Mer fravær', text: 'Sirkelen lukker seg. Gapet vokser, og det blir stadig vanskeligere å komme tilbake.' },
      { step: '06', title: 'Større kunnskapsgap', text: 'Hvert år uten tilstrekkelig leseopplæring forsterker gapet. Det som startet som et lite hull blir til en uoverstigelig mur.' },
    ],
    problemBody: 'Konvensjonelle løsninger — lekser, ekstra tid i klassen, generiske apper med gamification — treffer ikke denne gruppen. De forutsetter at grunnmuren er på plass. Det er den ikke.',

    solTag: 'Løsningen',
    solH2: 'Diagnostisér gapet. Fyll det. Gå videre.',
    solBody: 'Ketchup er bygget fra grunnen for barn som har mistet fotfeste i leseopplæringen. I stedet for å forutsette at barnet er på nivå med klassen, starter appen med å finne ut nøyaktig hvor barnet faktisk er.',
    solCards: [
      { icon: '🔍', title: 'Diagnostisér', text: 'En kort innledende kartlegging identifiserer nøyaktig hvilke leseferdigheter som mangler — fra enkeltbokstaver til lengre tekster.' },
      { icon: '📖', title: 'Undervise', text: 'Korte, fokuserte læringsøkter på akkurat det nivået barnet trenger. Ingen for lette oppgaver, ingen for vanskelige.' },
      { icon: '✓', title: 'Test og gå videre', text: 'Barnet må demonstrere mestring (90 % nøyaktighet på tvers av fem økter) før neste nivå låses opp. Ingen snarveier.' },
      { icon: '📚', title: 'Forskningsbasert', text: 'Denne metoden har sterk støtte i nyere forskning, som viser at mestringsbasert progresjon gir raskere læring.', link: '#forskning', linkText: 'Les om forskningen ↓' },
    ],
    levelsTitle: 'Seks lesesteg — fra bokstav til mester',
    levelsBody: 'Hvert steg er kalibrert mot Lix-systemet for norsk tekstkompleksitet. Barnet ser aldri et tall — bare sitt eget navn på steget det er på.',
    adhdTitle: 'ADHD-vennlig design',
    adhdItems: [
      'Ren, minimalistisk visning — én ting om gangen',
      'Ingen gamification, poeng eller rangeringer',
      'Korte, avsluttede øktformat',
      'Varm visuell stil som skaper trygghet, ikke press',
      'Ingen push-varsler eller addiksjonsmønstre',
      'Ingen distraherende animasjoner eller lyder',
    ],

    techTag: 'Teknologien',
    techH2: 'Norsk AI. Strengt personvern.',
    techBody: 'Ketchup kombinerer norsk språkteknologi med strenge personverngarantier — bygget for å tilfredsstille kravene til behandling av barns persondata.',
    techCards: [
      { icon: '🎙', title: 'Norsk talegjenkjenning', text: 'Høytlesing vurderes av nb-whisper — en norsk talegjenkjenningsmodell fra NbAiLab, kjørt på egne EU-servere.' },
      { icon: '🤖', title: 'AI-leseveileder', text: 'Tilpasset tilbakemelding og spørsmål genereres av Mistral — et europeisk språkmodell-selskap med EU-databehandling.' },
      { icon: '🔒', title: 'Null datalagring i USA', text: 'All inferens skjer på Hetzner-infrastruktur i EU. Ingen data sendes til amerikanske skytjenester.' },
      { icon: '🗑', title: 'Lyd slettes umiddelbart', text: 'Lydopptak slettes umiddelbart etter transkripsjon. Ingen voiceprints. Ingen stemmeidentifikasjon. Ingen emosjonsinferens.' },
      { icon: '📋', title: 'GDPR Art. 6 — ikke Art. 9', text: 'Siden lyd kun brukes til tekstgjenkjenning og slettes umiddelbart, klassifiseres det som ordinær persondata — ikke biometrisk særkategori.' },
      { icon: '🚫', title: 'EU AI Act-samsvarende', text: 'Appen gjenkjenner aldri emosjoner fra stemme eller tekst — forbudt i utdanningssammenheng under EU AI Act Art. 27.' },
    ],

    resTag: 'Forskning',
    resH2: 'Forankret i norsk leseforskning',
    resBody: 'Ketchups pedagogiske modell er bygget på etablert forskning om leseutvikling, mestringsbasert læring og konsekvensene av langvarig skolefravær.',
    resCards: [
      { title: 'Mestringsbasert progressjon', text: 'Forskning viser konsekvent at eksponering alene ikke gir varig lesemestring. Barnet må demonstrere aktiv mestring på hvert steg før det kan gå videre. Ketchups krav om 90 % nøyaktighet på tvers av fem ulike tekster per nivå er direkte basert på dette prinsippet.' },
      { title: 'Systematisk ferdighetsbygging', text: 'Lix-systemet — brukt i norsk skole for å vurdere tekstkompleksitet — danner grunnlaget for Ketchups seks lesesteg. Progressjonen er ikke vilkårlig: hvert steg tilsvarer en veldefinert ferdighetssone der barnet er mottakelig for ny kunnskap uten å bli overveldet.' },
      { title: 'Skolefravær og kunnskapsgap', text: 'Norsk forskning dokumenterer at langvarig fravær — særlig i grunnskolen — skaper kumulerende kunnskapsgap som er svært vanskelige å hente inn uten målrettet intervensjon. Tradisjonell oppfølging forutsetter at barnet er tilstede og på nivå; Ketchup er designet for den virkeligheten der det ikke er det.' },
      { title: 'ADHD og kognitivt design', text: 'En uforholdsmessig stor andel barn med høyt skolefravær har diagnoser eller symptomer innenfor ADHD-spekteret. Forskning på kognitive hjelpemidler for ADHD peker entydig mot: minimal distraksjonseksponering, tydelig oppgavestruktur, korte og avsluttede sekvenser, og fravær av belønningsmekanismer som skaper avhengighet fremfor mestring.' },
    ],
    corpusTitle: 'Tekstkorpus',
    corpusBody: 'Ketchup-appen inkluderer et kurert korpus på over 2 250 norske tekster, kategorisert etter lesesteg, Lix-score og emnedomene — fordelt på 10 kunnskapsdomener fra romforskning til matlaging. Alle tekster er åpenlisensiert og tilpasset norsk leseopplæring.',

    planTag: 'Planen',
    planH2: 'Fra beta til lansering',
    planBody: 'Utviklingen er i rute. Kjerneappen — inkludert høytlesingsassessment, forståelsesspørsmål, foreldreportal og full sikkerhetshardening — er ferdig utviklet.',
    planItems: [
      { period: 'H1 2026', status: 'Pågår', title: 'Utvikling pågår', text: '' },
      { period: 'H2 2026', status: 'Planlagt', title: 'Beta', text: 'Kontrollert beta med utvalgte familier og fagpersoner. Fokus på brukeropplevelse, teknisk robusthet og pedagogisk effekt.' },
      { period: '2027', status: 'Mål', title: 'Lansering', text: 'Offentlig lansering på App Store og Google Play.' },
    ],

    footerBy: 'Et prosjekt av',
  },
  en: {
    heroTag: 'Reading app for children',
    heroH1a: 'Reading support that meets',
    heroH1b: 'the child where they are.',
    heroSub: 'An AI-powered reading tutor for children with high school absence — designed to fill knowledge gaps, one step at a time.',
    heroCta: 'Beta H2 2026',
    heroKompis: '🐙 With Kompis',

    problemTag: 'The Problem',
    problemH2: 'The vicious cycle',
    problemCards: [
      { step: '01', title: 'Absence', text: 'The child misses a lot of school — for health, social, or psychological reasons.' },
      { step: '02', title: 'Knowledge gap', text: 'Absence leads to gaps in foundational skills. The longer they\'re away, the bigger the gap.' },
      { step: '03', title: 'Lack of mastery', text: 'When the child is present, they can\'t keep up. Reading and tasks feel overwhelming.' },
      { step: '04', title: 'Anxiety and withdrawal', text: 'Feelings of inadequacy create anxiety. The child withdraws — or acts out to hide it.' },
      { step: '05', title: 'More absence', text: 'The cycle closes. The gap grows, and it becomes increasingly hard to return.' },
      { step: '06', title: 'Larger knowledge gap', text: 'Every year without adequate reading instruction widens the gap. What started as a small hole becomes an insurmountable wall.' },
    ],
    problemBody: 'Conventional solutions — homework, extra time in class, generic apps with gamification — miss this group entirely. They assume the foundation is in place. It isn\'t.',

    solTag: 'The Solution',
    solH2: 'Diagnose the gap. Fill it. Move on.',
    solBody: 'Ketchup is built from the ground up for children who have lost their footing in reading instruction. Instead of assuming the child is at grade level, the app starts by finding out exactly where the child actually is.',
    solCards: [
      { icon: '🔍', title: 'Diagnose', text: 'A short initial assessment identifies exactly which reading skills are missing — from individual letters to longer texts.' },
      { icon: '📖', title: 'Teach', text: 'Short, focused learning sessions at exactly the level the child needs. Nothing too easy, nothing too hard.' },
      { icon: '✓', title: 'Test and move on', text: 'The child must demonstrate mastery (90% accuracy across five sessions) before the next level unlocks. No shortcuts.' },
      { icon: '📚', title: 'Research-based', text: 'This method has strong support in recent research, showing that mastery-based progression leads to faster learning.', link: '#forskning', linkText: 'Read the research ↓' },
    ],
    levelsTitle: 'Six reading levels — from letter to master',
    levelsBody: 'Each level is calibrated against the Lix system for Norwegian text complexity. The child never sees a number — just their own name for the level they\'re on.',
    adhdTitle: 'ADHD-friendly design',
    adhdItems: [
      'Clean, minimalist display — one thing at a time',
      'No gamification, points, or rankings',
      'Short, contained session format',
      'Warm visual style that creates security, not pressure',
      'No push notifications or addictive patterns',
      'No distracting animations or sounds',
    ],

    techTag: 'Technology',
    techH2: 'Norwegian AI. Strict privacy.',
    techBody: 'Ketchup combines Norwegian language technology with strict privacy guarantees — built to meet the requirements for processing children\'s personal data.',
    techCards: [
      { icon: '🎙', title: 'Norwegian speech recognition', text: 'Oral reading is assessed by nb-whisper — a Norwegian speech recognition model from NbAiLab, run on our own EU servers.' },
      { icon: '🤖', title: 'AI reading tutor', text: 'Tailored feedback and questions are generated by Mistral — a European language model company with EU data processing.' },
      { icon: '🔒', title: 'Zero US data storage', text: 'All inference happens on Hetzner infrastructure in the EU. No data is sent to American cloud services.' },
      { icon: '🗑', title: 'Audio deleted immediately', text: 'Recordings are deleted immediately after transcription. No voiceprints. No speaker identification. No emotion inference.' },
      { icon: '📋', title: 'GDPR Art. 6 — not Art. 9', text: 'Since audio is only used for speech recognition and deleted immediately, it is classified as ordinary personal data — not biometric special category.' },
      { icon: '🚫', title: 'EU AI Act compliant', text: 'The app never infers emotions from voice or text — prohibited in educational settings under EU AI Act Art. 27.' },
    ],

    resTag: 'Research',
    resH2: 'Grounded in Norwegian reading research',
    resBody: 'Ketchup\'s pedagogical model is built on established research on reading development, mastery-based learning, and the consequences of prolonged school absence.',
    resCards: [
      { title: 'Mastery-based progression', text: 'Research consistently shows that exposure alone does not produce lasting reading mastery. The child must demonstrate active mastery at each step before moving on. Ketchup\'s requirement of 90% accuracy across five different texts per level is directly based on this principle.' },
      { title: 'Systematic skills development', text: 'The Lix system — used in Norwegian schools to assess text complexity — forms the basis of Ketchup\'s six reading levels. The progression is not arbitrary: each level corresponds to a well-defined skill zone where the child is receptive to new knowledge without being overwhelmed.' },
      { title: 'School absence and knowledge gaps', text: 'Norwegian research documents that prolonged absence — particularly in primary school — creates cumulative knowledge gaps that are very difficult to recover without targeted intervention. Traditional follow-up assumes the child is present and at level; Ketchup is designed for the reality where they are not.' },
      { title: 'ADHD and cognitive design', text: 'A disproportionate share of children with high school absence have diagnoses or symptoms within the ADHD spectrum. Research on cognitive aids for ADHD points unambiguously to: minimal distraction exposure, clear task structure, short and contained sequences, and the absence of reward mechanisms that create dependency rather than mastery.' },
    ],
    corpusTitle: 'Text corpus',
    corpusBody: 'Ketchup includes a curated corpus of over 2,250 Norwegian texts, categorised by reading level, Lix score, and subject domain — spanning 10 knowledge domains from space exploration to cooking. All texts are openly licensed and adapted for Norwegian reading instruction.',

    planTag: 'The Plan',
    planH2: 'From beta to launch',
    planBody: 'Development is on track. The core app — including oral reading assessment, comprehension questions, parent portal, and full security hardening — is complete.',
    planItems: [
      { period: 'H1 2026', status: 'Ongoing', title: 'Development in progress', text: '' },
      { period: 'H2 2026', status: 'Planned', title: 'Beta', text: 'Controlled beta with selected families and professionals. Focus on user experience, technical robustness, and pedagogical impact.' },
      { period: '2027', status: 'Target', title: 'Launch', text: 'Public launch on App Store and Google Play.' },
    ],

    footerBy: 'A project by',
  },
};

const planStatusColor = (status) => {
  if (status === 'Pågår' || status === 'Ongoing') return K.amber;
  if (status === 'Planlagt' || status === 'Planned') return K.red;
  return K.muted;
};

const KetchupPage = () => {
  const [lang, setLang] = useState('no');
  const c = content[lang];

  return (
    <div style={{ background: K.bg, minHeight: '100vh', fontFamily: K.font }}>

      {/* Nav */}
      <nav style={{
        background: K.bg,
        borderBottom: `1.5px solid ${K.surfaceHigh}`,
        padding: '0 32px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <div style={{
          maxWidth: 900,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 64,
        }}>
          <span style={{ fontFamily: K.font, fontWeight: 700, fontSize: '1.375rem', color: K.red, letterSpacing: '-0.02em' }}>
            Ketchup
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <button
              onClick={() => setLang(lang === 'no' ? 'en' : 'no')}
              style={{
                background: 'none',
                border: `1.5px solid ${K.surfaceHigh}`,
                color: K.muted,
                fontFamily: K.font,
                fontWeight: 600,
                fontSize: '0.8125rem',
                padding: '5px 14px',
                borderRadius: 999,
                cursor: 'pointer',
                letterSpacing: '0.05em',
              }}
            >
              {lang === 'no' ? 'EN' : 'NO'}
            </button>
            <a href="https://ktrl.no" style={{ fontFamily: K.font, fontSize: '0.875rem', color: K.muted, textDecoration: 'none' }}>
              ktrl.no
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <Section>
        <Tag>{c.heroTag}</Tag>
        <h1 style={{
          fontFamily: K.font,
          fontWeight: 700,
          fontSize: 'clamp(2.25rem, 5vw, 3.75rem)',
          color: K.text,
          margin: '0 0 24px 0',
          lineHeight: 1.1,
          letterSpacing: '-0.03em',
        }}>
          <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{c.heroH1a}</span>
          <span style={{ display: 'block' }}>{c.heroH1b}</span>
        </h1>
        <Body style={{ fontSize: '1.175rem', color: K.muted, marginBottom: 40, maxWidth: 600 }}>
          {c.heroSub}
        </Body>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ background: K.red, color: '#fff', fontFamily: K.font, fontWeight: 600, fontSize: '0.9375rem', padding: '12px 28px', borderRadius: 999 }}>
            {c.heroCta}
          </span>
          <span style={{ background: 'transparent', border: `2px solid ${K.surfaceHigh}`, color: K.muted, fontFamily: K.font, fontWeight: 500, fontSize: '0.9375rem', padding: '12px 28px', borderRadius: 999 }}>
            {c.heroKompis}
          </span>
        </div>
      </Section>

      <Divider />

      {/* Problemet */}
      <Section>
        <Tag>{c.problemTag}</Tag>
        <H2>{c.problemH2}</H2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 32 }}>
          {c.problemCards.map(({ step, title, text }) => (
            <Card key={step}>
              <div style={{ color: K.amber, fontFamily: K.font, fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: 8 }}>{step}</div>
              <div style={{ fontFamily: K.font, fontWeight: 600, fontSize: '1.0625rem', color: K.text, marginBottom: 8 }}>{title}</div>
              <div style={{ fontFamily: K.font, fontSize: '0.9375rem', lineHeight: 1.65, color: K.muted }}>{text}</div>
            </Card>
          ))}
        </div>
        <Body>{c.problemBody}</Body>
      </Section>

      <Divider />

      {/* Løsningen */}
      <Section>
        <Tag>{c.solTag}</Tag>
        <H2>{c.solH2}</H2>
        <Body>{c.solBody}</Body>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 40 }}>
          {c.solCards.map(({ icon, title, text, link, linkText }) => (
            <Card key={title} style={{ background: K.surface }}>
              <div style={{ fontSize: '2rem', marginBottom: 16 }}>{icon}</div>
              <div style={{ fontFamily: K.font, fontWeight: 600, fontSize: '1.125rem', color: K.text, marginBottom: 8 }}>{title}</div>
              <div style={{ fontFamily: K.font, fontSize: '0.9375rem', lineHeight: 1.65, color: K.muted }}>{text}</div>
              {link && (
                <a href={link} style={{ display: 'inline-block', marginTop: 12, fontFamily: K.font, fontSize: '0.875rem', fontWeight: 600, color: K.red, textDecoration: 'none' }}>
                  {linkText}
                </a>
              )}
            </Card>
          ))}
        </div>

        <Card style={{ background: K.surfaceHigh, padding: '28px 36px' }}>
          <div style={{ fontFamily: K.font, fontWeight: 600, fontSize: '1rem', color: K.text, marginBottom: 12 }}>{c.levelsTitle}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {['Bokstav', 'Ord', 'Setning', 'Avsnitt', 'Tekst', 'Mester'].map((level, i) => (
              <span key={level} style={{
                background: i === 0 ? K.red : K.surface,
                color: i === 0 ? '#fff' : K.text,
                fontFamily: K.font,
                fontWeight: 500,
                fontSize: '0.875rem',
                padding: '6px 16px',
                borderRadius: 999,
                border: `1.5px solid ${i === 0 ? K.red : K.surfaceHigh}`,
              }}>
                {level}
              </span>
            ))}
          </div>
          <Body style={{ marginTop: 16, marginBottom: 0, fontSize: '0.9375rem' }}>{c.levelsBody}</Body>
        </Card>

        <div style={{ marginTop: 40 }}>
          <div style={{ fontFamily: K.font, fontWeight: 600, fontSize: '1rem', color: K.text, marginBottom: 16 }}>{c.adhdTitle}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            {c.adhdItems.map((text, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ color: K.red, fontWeight: 700, marginTop: 1 }}>✓</span>
                <span style={{ fontFamily: K.font, fontSize: '0.9375rem', color: K.muted, lineHeight: 1.55 }}>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Divider />

      {/* Teknologien */}
      <Section>
        <Tag>{c.techTag}</Tag>
        <H2>{c.techH2}</H2>
        <Body>{c.techBody}</Body>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {c.techCards.map(({ icon, title, text }) => (
            <Card key={title}>
              <div style={{ fontSize: '1.5rem', marginBottom: 12 }}>{icon}</div>
              <div style={{ fontFamily: K.font, fontWeight: 600, fontSize: '1rem', color: K.text, marginBottom: 6 }}>{title}</div>
              <div style={{ fontFamily: K.font, fontSize: '0.9rem', lineHeight: 1.65, color: K.muted }}>{text}</div>
            </Card>
          ))}
        </div>
      </Section>

      <Divider />

      {/* Forskning */}
      <Section id="forskning">
        <Tag>{c.resTag}</Tag>
        <H2>{c.resH2}</H2>
        <Body>{c.resBody}</Body>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 40 }}>
          {c.resCards.map(({ title, text }) => (
            <Card key={title} style={{ paddingTop: 28, paddingBottom: 28 }}>
              <div style={{ fontFamily: K.font, fontWeight: 600, fontSize: '1.0625rem', color: K.text, marginBottom: 10 }}>{title}</div>
              <div style={{ fontFamily: K.font, fontSize: '0.9375rem', lineHeight: 1.7, color: K.muted }}>{text}</div>
            </Card>
          ))}
        </div>
        <Card style={{ background: K.surfaceHigh }}>
          <div style={{ fontFamily: K.font, fontWeight: 600, fontSize: '0.9375rem', color: K.text, marginBottom: 8 }}>{c.corpusTitle}</div>
          <Body style={{ marginBottom: 0, fontSize: '0.9375rem' }}>{c.corpusBody}</Body>
        </Card>
      </Section>

      <Divider />

      {/* Planen */}
      <Section>
        <Tag>{c.planTag}</Tag>
        <H2>{c.planH2}</H2>
        <Body>{c.planBody}</Body>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 40 }}>
          {c.planItems.map(({ period, status, title, text }) => (
            <Card key={period} style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 80 }}>
                <div style={{ fontFamily: K.font, fontWeight: 700, fontSize: '0.875rem', color: K.text }}>{period}</div>
                <div style={{ fontFamily: K.font, fontWeight: 600, fontSize: '0.75rem', color: planStatusColor(status), marginTop: 2 }}>{status}</div>
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontFamily: K.font, fontWeight: 600, fontSize: '1.0625rem', color: K.text, marginBottom: text ? 4 : 0 }}>{title}</div>
                {text && <div style={{ fontFamily: K.font, fontSize: '0.9375rem', lineHeight: 1.65, color: K.muted }}>{text}</div>}
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {/* Footer */}
      <div style={{ borderTop: `1.5px solid ${K.surfaceHigh}`, background: K.surface }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <span style={{ fontFamily: K.font, fontWeight: 700, fontSize: '1.125rem', color: K.red }}>Ketchup</span>
          <span style={{ fontFamily: K.font, fontSize: '0.875rem', color: K.muted }}>
            {c.footerBy}{' '}
            <a href="https://ktrl.no" style={{ color: K.muted, textDecoration: 'underline' }}>Jakob B. Bronebakk</a>
          </span>
        </div>
      </div>

    </div>
  );
};

export default KetchupPage;
